import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronRight, Home } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { getContentfulEntriesByEntryType } from '@/services/contentful'
import type { ContentfulRichTextNode } from '@/types/contentful'

function formatDate(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return new Intl.DateTimeFormat('sv-SE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date)
}

function applyMarks(textNode: ContentfulRichTextNode): ReactNode {
  let current: ReactNode = textNode.value ?? ''
  for (const mark of textNode.marks ?? []) {
    if (mark.type === 'bold') current = <strong>{current}</strong>
    else if (mark.type === 'italic') current = <em>{current}</em>
    else if (mark.type === 'underline') current = <u>{current}</u>
    else if (mark.type === 'code') current = <code className="rounded bg-muted px-1 py-0.5">{current}</code>
  }
  return current
}

function renderRichNode(node: ContentfulRichTextNode, key: string): ReactNode {
  if (node.nodeType === 'text') {
    return <span key={key}>{applyMarks(node)}</span>
  }

  const children = (node.content ?? []).map((child, index) =>
    renderRichNode(child, `${key}-${index}`)
  )

  if (node.nodeType === 'paragraph') {
    return <p key={key} className="text-sm leading-7 text-foreground">{children}</p>
  }
  if (node.nodeType === 'heading-1') return <h1 key={key} className="text-3xl font-semibold">{children}</h1>
  if (node.nodeType === 'heading-2') return <h2 key={key} className="text-2xl font-semibold">{children}</h2>
  if (node.nodeType === 'heading-3') return <h3 key={key} className="text-xl font-semibold">{children}</h3>
  if (node.nodeType === 'unordered-list') return <ul key={key} className="list-disc space-y-1 pl-6">{children}</ul>
  if (node.nodeType === 'ordered-list') return <ol key={key} className="list-decimal space-y-1 pl-6">{children}</ol>
  if (node.nodeType === 'list-item') return <li key={key}>{children}</li>
  if (node.nodeType === 'blockquote') return <blockquote key={key} className="border-l-4 pl-4 italic text-muted-foreground">{children}</blockquote>
  if (node.nodeType === 'hr') return <hr key={key} className="my-4" />

  return <>{children}</>
}

function RichTextContent({ document }: { document?: ContentfulRichTextNode }) {
  if (!document) {
    return null
  }
  return (
    <div className="space-y-4">
      {(document.content ?? []).map((node, index) => renderRichNode(node, `root-${index}`))}
    </div>
  )
}

export function ContentEntryPage() {
  const { entryType, entryId } = useParams<{
    entryType: 'whats-new' | 'release-note'
    entryId: string
  }>()

  const isValidType = entryType === 'whats-new' || entryType === 'release-note'

  const entriesQuery = useQuery({
    queryKey: ['welcome-entry-list', entryType],
    queryFn: () => getContentfulEntriesByEntryType(entryType!, 100),
    enabled: Boolean(entryId && isValidType),
  })

  if (!isValidType) {
    return <div className="py-12 text-sm text-muted-foreground">Invalid entry type.</div>
  }

  if (entriesQuery.isLoading) {
    return <div className="py-12 text-sm text-muted-foreground">Loading entry...</div>
  }

  if (entriesQuery.isError || !entriesQuery.data) {
    return <div className="py-12 text-sm text-muted-foreground">Entry not found.</div>
  }

  const entries = entriesQuery.data
  const currentIndex = entries.findIndex((candidate) => candidate.id === entryId)

  if (currentIndex === -1) {
    return <div className="py-12 text-sm text-muted-foreground">Entry not found.</div>
  }

  const entry = entries[currentIndex]
  const newerEntry = currentIndex > 0 ? entries[currentIndex - 1] : null
  const olderEntry = currentIndex < entries.length - 1 ? entries[currentIndex + 1] : null

  return (
    <main className="container mx-auto space-y-6 py-8">
      <nav className="flex items-center gap-2 text-sm text-on-canvas-muted">
        <Link to="/" className="inline-flex items-center gap-1 hover:text-on-canvas">
          <Home className="h-4 w-4" />
          Welcome
        </Link>
        <ChevronRight className="h-4 w-4" />
        <span className="capitalize">{entryType.replace('-', ' ')}</span>
        <ChevronRight className="h-4 w-4" />
        <span className="text-on-canvas">{entry.title}</span>
      </nav>

      <Card>
        <CardHeader className="space-y-3">
          <div className="text-sm text-muted-foreground">{formatDate(entry.publishedAt)}</div>
          <CardTitle className="text-2xl">{entry.title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className={entry.imageUrl ? 'grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start' : ''}>
            <div className="space-y-6">
              {entry.richDescription ? (
                <RichTextContent document={entry.richDescription} />
              ) : (
                <div className="whitespace-pre-wrap text-sm leading-7 text-foreground">
                  {entry.description || 'No description provided.'}
                </div>
              )}
              {entry.url && (
                <a
                  href={entry.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex text-sm font-medium text-primary hover:underline"
                >
                  Open related link
                </a>
              )}
            </div>
            {entry.imageUrl && (
              <img
                src={entry.imageUrl}
                alt={entry.title}
                className="h-64 w-full rounded-md border object-cover lg:sticky lg:top-6 lg:h-auto"
              />
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-3 sm:grid-cols-2">
        {newerEntry ? (
          <Link
            to={`/entry/${entryType}/${newerEntry.id}`}
            className="rounded-panel border border-canvas-border bg-surface p-4 shadow-panel transition-colors hover:border-primary/40"
          >
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Newer post</p>
            <p className="mt-1 text-sm font-medium text-foreground">{newerEntry.title}</p>
          </Link>
        ) : (
          <div className="rounded-panel border border-canvas-border bg-surface p-4 shadow-panel opacity-60">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Newer post</p>
            <p className="mt-1 text-sm text-muted-foreground">This is the latest post.</p>
          </div>
        )}

        {olderEntry ? (
          <Link
            to={`/entry/${entryType}/${olderEntry.id}`}
            className="rounded-panel border border-canvas-border bg-surface p-4 shadow-panel text-right transition-colors hover:border-primary/40"
          >
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Older post</p>
            <p className="mt-1 text-sm font-medium text-foreground">{olderEntry.title}</p>
          </Link>
        ) : (
          <div className="rounded-panel border border-canvas-border bg-surface p-4 shadow-panel text-right opacity-60">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Older post</p>
            <p className="mt-1 text-sm text-muted-foreground">This is the oldest post.</p>
          </div>
        )}
      </div>
    </main>
  )
}
